import { Params } from '@angular/router';
import { AnalyticsFilters, AnalyticsTimePeriod, defaultAnalyticsSearchFilters } from '@app/types/analytics-filters';
import { IncidentsFilters, defaultIncidentsSearchFilters } from '@app/types/incidents-filters';
import {
  AnalyticsModel,
  AnalyticsBusinessSectorVariant,
  AnalyticsCountryVariant,
  AnalyticsContinentVariant,
  AnalyticsComparisonModel,
  AnalyticsRequestModel,
} from '@app/models/analytics.model';
import { IncidentsFiltersModel } from '@app/models/incident-filters.model';

import { formatAsDateTimeStringIgnoringTheTime } from '../datetime/datetime.utils';
import { SortDirection } from '@angular/material/sort';
import { SearchMode } from '@app/enums/search-mode';
import { BusinessSector, BusinessSectorDescriptor } from '@app/enums/business-sector';
import { CountriesByContinent, Country, CountryDescriptor } from '@app/enums/country';
import { Continent, ContinentDescriptor } from '@app/enums/continent';
import { EntitySize, EntitySizeDescriptor } from '@app/enums/entity-size';
import { Motivation } from '@app/enums/motivation';
import { ActorType } from '@app/enums/actor-type';
import { MediaFileModel } from '@app/models/media-file.model';
import { IncidentType } from '@app/enums/incident-type';
import { IncidentStatus } from '@app/enums/incident-status';

const calculateIncidentDifference = (incidentCountA: number, incidentCountB: number) => {
  const ratioNumeric = incidentCountB - incidentCountA;
  const high = Math.max(incidentCountA, incidentCountB);
  const ratioProgress = (Math.abs(ratioNumeric) / high) * 100;
  const trend = ratioNumeric < 0 ? 'decreasing' : 'increasing';

  return {
    incidentCountA,
    incidentCountB,
    ratioNumeric,
    ratioProgress,
    trend,
  };
};

export const createAnalyticsComparisonData = (analytics?: AnalyticsModel): AnalyticsComparisonModel | undefined => {
  if (!analytics) {
    return;
  }

  return {
    entity: createAnalyticsEntityData(analytics),
    country: createAnalyticsCountryData(analytics),
    continent: createAnalyticsContinentData(analytics),
  };
};

export const createAnalyticsEntityData = (analytics: AnalyticsModel): AnalyticsBusinessSectorVariant[] => {
  if (!analytics || !analytics?.analyticsPeriodA || !analytics?.analyticsPeriodB) {
    return [];
  }

  const { analyticsPeriodA, analyticsPeriodB } = analytics;
  const { incidentsCountByBusinessSector: incidentsCountByBusinessSectorA } = analyticsPeriodA!;
  const { incidentsCountByBusinessSector: incidentsCountByBusinessSectorB } = analyticsPeriodB!;

  const allBusinssSectors = new Set([
    ...Object.keys(incidentsCountByBusinessSectorA),
    ...Object.keys(incidentsCountByBusinessSectorB),
  ]);
  const availableBusinessSectors = [...allBusinssSectors].filter((bs) =>
    Object.keys(BusinessSectorDescriptor).includes(bs),
  );

  return availableBusinessSectors.map((key: unknown) => {
    const incidentCountA = incidentsCountByBusinessSectorA[key as keyof typeof BusinessSector] ?? 0;
    const incidentCountB = incidentsCountByBusinessSectorB[key as keyof typeof BusinessSector] ?? 0;
    const counts = calculateIncidentDifference(incidentCountA, incidentCountB);

    return {
      businessSector: key,
      businessSectorLabel: BusinessSectorDescriptor[key as keyof typeof BusinessSector],
      ...counts,
    } as AnalyticsBusinessSectorVariant;
  });
};

export const createAnalyticsCountryData = (analytics: AnalyticsModel): AnalyticsCountryVariant[] => {
  if (!analytics || !analytics?.analyticsPeriodA || !analytics?.analyticsPeriodB) {
    return [];
  }

  const { analyticsPeriodA, analyticsPeriodB } = analytics;
  const { incidentsCountByCountryCode: incidentsCountByCountryCodeA } = analyticsPeriodA!;
  const { incidentsCountByCountryCode: incidentsCountByCountryCodeB } = analyticsPeriodB!;

  const allCountries = new Set([
    ...Object.keys(incidentsCountByCountryCodeA),
    ...Object.keys(incidentsCountByCountryCodeB),
  ]);
  const availableCountries = [...allCountries].filter((country) => Object.keys(CountryDescriptor).includes(country));

  return availableCountries.map((country: string) => {
    const incidentCountA = incidentsCountByCountryCodeA[country as Country] ?? 0;
    const incidentCountB = incidentsCountByCountryCodeB[country as Country] ?? 0;
    const counts = calculateIncidentDifference(incidentCountA, incidentCountB);

    return {
      country,
      countryLabel: CountryDescriptor[country as keyof typeof Country],
      ...counts,
    } as AnalyticsCountryVariant;
  });
};

export const createAnalyticsContinentData = (analytics: AnalyticsModel): AnalyticsContinentVariant[] => {
  if (!analytics || !analytics?.analyticsPeriodA || !analytics?.analyticsPeriodB) {
    return [];
  }

  const { analyticsPeriodA, analyticsPeriodB } = analytics;
  const { incidentsCountByContinent: incidentsCountByContinentA } = analyticsPeriodA!;
  const { incidentsCountByContinent: incidentsCountByContinentB } = analyticsPeriodB!;

  const allContinents = new Set([
    ...Object.keys(incidentsCountByContinentA),
    ...Object.keys(incidentsCountByContinentB),
  ]);
  const availableContinents = [...allContinents].filter((continent) =>
    Object.keys(ContinentDescriptor).includes(continent),
  );

  return availableContinents.map((continent: string) => {
    const incidentCountA = incidentsCountByContinentA[continent as Continent] ?? 0;
    const incidentCountB = incidentsCountByContinentB[continent as Continent] ?? 0;
    const counts = calculateIncidentDifference(incidentCountA, incidentCountB);

    return {
      continent,
      continentLabel: ContinentDescriptor[continent as keyof typeof Continent],
      ...counts,
    } as AnalyticsContinentVariant;
  });
};

export function mapRouteParamsToAnalyticsFilters(params: Params): {
  filters: AnalyticsFilters;
} {
  const filters: AnalyticsFilters = structuredClone(defaultAnalyticsSearchFilters);

  if (params['timeperiod1.dateStart']) {
    filters.timeperiod1.value.dateStart = formatAsDateTimeStringIgnoringTheTime(params['timeperiod1.dateStart']);
  }

  if (params['timeperiod1.dateEnd']) {
    filters.timeperiod1.value.dateEnd = formatAsDateTimeStringIgnoringTheTime(params['timeperiod1.dateEnd']);
  }

  if (params['timeperiod2.dateStart']) {
    filters.timeperiod2.value.dateStart = formatAsDateTimeStringIgnoringTheTime(params['timeperiod2.dateStart']);
  }

  if (params['timeperiod2.dateEnd']) {
    filters.timeperiod2.value.dateEnd = formatAsDateTimeStringIgnoringTheTime(params['timeperiod2.dateEnd']);
  }

  if (params['revenueFrom']) {
    filters.revenueFrom.value = params['revenueFrom'];
  }

  if (params['revenueTo']) {
    filters.revenueTo.value = params['revenueTo'];
  }

  if (params['entitySizes']) {
    const entitySizesValues = params['entitySizes'].split(',') as string[];
    const entitiesInParams: EntitySize[] = entitySizesValues.filter((entity) =>
      Object.keys(EntitySizeDescriptor).includes(entity),
    ) as EntitySize[];

    filters.entitySizes.value = [...entitiesInParams];
  }

  if (params['businessSectors']) {
    const businessSectorsValues = params['businessSectors'].split(',') as string[];
    const businessSectorsInParams: BusinessSector[] = businessSectorsValues.filter((sector) =>
      Object.keys(BusinessSectorDescriptor).includes(sector),
    ) as BusinessSector[];

    filters.businessSectors.value = [...businessSectorsInParams];
  }

  if (params['geoLocation']) {
    const geoLocationValues = params['geoLocation'].split(',') as string[];

    const countriesInParams: Country[] = geoLocationValues.filter((code) =>
      Object.keys(CountryDescriptor).includes(code),
    ) as Country[];
    const continentsInParams: Continent[] = geoLocationValues.filter((code) =>
      Object.keys(ContinentDescriptor).includes(code),
    ) as Continent[];

    filters.continentCodes.value = continentsInParams;

    const continentAdjustments = continentsInParams.flatMap((continent: Continent) =>
      Array.from(CountriesByContinent[continent]).map((c) => `${continent}:${c}`),
    );
    const continentAdjustedCountries = Object.entries(CountriesByContinent)
      .map(([continent, countries]) =>
        countriesInParams.map((c) => (countries.has(c) ? `${continent}:${c}` : void 0)).filter((c) => !!c),
      )
      .filter((c) => !!c.length)
      .flat() as string[];

    filters.countryCodes.value = [...continentAdjustedCountries, ...continentAdjustments];
  }

  return { filters };
}

export function mapAnalyticsFiltersToRouteParams(filters: AnalyticsFilters): Params {
  const geoLocations: string[] = getGeoLocationsFromFilters(filters.continentCodes.value, filters.countryCodes.value);

  const allSelectedEntitySizes = filters.entitySizes.value;
  const allSelectedBusinessSectors = filters.businessSectors.value;

  return {
    ['timeperiod1.dateStart']: formatAsDateTimeStringIgnoringTheTime(filters.timeperiod1.value.dateStart) || undefined,
    ['timeperiod1.dateEnd']: formatAsDateTimeStringIgnoringTheTime(filters.timeperiod1.value.dateEnd) || undefined,
    ['timeperiod2.dateStart']: formatAsDateTimeStringIgnoringTheTime(filters.timeperiod2.value.dateStart) || undefined,
    ['timeperiod2.dateEnd']: formatAsDateTimeStringIgnoringTheTime(filters.timeperiod2.value.dateEnd) || undefined,
    revenueFrom: filters.revenueFrom.value || undefined,
    revenueTo: filters.revenueTo.value || undefined,
    // eslint-disable-next-line no-extra-boolean-cast
    ...(!!allSelectedEntitySizes.length ? { entitySizes: allSelectedEntitySizes.join(',') } : {}),
    // eslint-disable-next-line no-extra-boolean-cast
    ...(!!allSelectedBusinessSectors.length ? { businessSectors: allSelectedBusinessSectors.join(',') } : {}),
    geoLocation: geoLocations.length ? geoLocations.join(',') : undefined,
  };
}

// This function takes the relevant filters applied in the analytics page and creates route parameters for the incidents list page.
export function mapAnalyticsFiltersToIncidentsPageRouteParams(
  filters: AnalyticsFilters,
  timePeriod: AnalyticsTimePeriod,
): Params {
  const geoLocations: string[] = getGeoLocationsFromFilters(filters.continentCodes.value, filters.countryCodes.value);

  const allSelectedEntitySizes = filters.entitySizes.value;
  const allSelectedBusinessSectors = filters.businessSectors.value;

  return {
    geoLocation: geoLocations.length ? geoLocations.join(',') : undefined,
    ...(allSelectedBusinessSectors.length ? { businessSector: allSelectedBusinessSectors.join(',') } : {}),
    ...(allSelectedEntitySizes.length ? { entitySize: allSelectedEntitySizes.join(',') } : {}),
    revenueFrom: filters.revenueFrom.value || undefined,
    revenueTo: filters.revenueTo.value || undefined,
    ...getPeriodStartAndEndDatesAsIncidentPageParams(filters, timePeriod),
    // Incident analytics page contains results only for active incidents. That's why we add "incidentDisabled" with false so the incidents page won't show the disabled ones.
    incidentDisabled: false,
  };
}

// Takes the analytics filters and a time period requirement and returns ready url params suitable for the incidents page.
export function getPeriodStartAndEndDatesAsIncidentPageParams(
  filters: AnalyticsFilters,
  timePeriod: AnalyticsTimePeriod,
): Params {
  return {
    ...(timePeriod === 'A'
      ? {
          dateStart: formatAsDateTimeStringIgnoringTheTime(filters.timeperiod1.value.dateStart),
          dateEnd: formatAsDateTimeStringIgnoringTheTime(filters.timeperiod1.value.dateEnd),
        }
      : {}),
    ...(timePeriod === 'B'
      ? {
          dateStart: formatAsDateTimeStringIgnoringTheTime(filters.timeperiod2.value.dateStart),
          dateEnd: formatAsDateTimeStringIgnoringTheTime(filters.timeperiod2.value.dateEnd),
        }
      : {}),
  };
}

export function mapAnalyticsFiltersToModel(filters: AnalyticsFilters): AnalyticsRequestModel {
  const entitySizes =
    Array.isArray(filters.entitySizes.value) && filters.entitySizes.value.length
      ? (filters.entitySizes.value as EntitySize[])
      : undefined;

  const businessSectors =
    Array.isArray(filters.businessSectors.value) && filters.businessSectors.value.length
      ? (filters.businessSectors.value as BusinessSector[])
      : undefined;

  const continentCodes =
    Array.isArray(filters.continentCodes.value) && filters.continentCodes.value.length
      ? (filters.continentCodes.value as Continent[])
      : undefined;

  // We need to split the countries as they have the continent written before
  const allSelectedCountries =
    Array.isArray(filters.countryCodes.value) && filters.countryCodes.value.length
      ? new Set(filters.countryCodes.value.map((c) => c.split(':')[1] as Country))
      : undefined;

  return {
    startDateAnalyticsPeriodA: filters.timeperiod1.value.dateStart || undefined,
    endDateAnalyticsPeriodA: filters.timeperiod1.value.dateEnd || undefined,
    startDateAnalyticsPeriodB: filters.timeperiod2.value.dateStart || undefined,
    endDateAnalyticsPeriodB: filters.timeperiod2.value.dateEnd || undefined,
    revenueFrom: !filters.revenueFrom.value ? undefined : parseFloat(filters.revenueFrom.value),
    revenueTo: !filters.revenueTo.value ? undefined : parseFloat(filters.revenueTo.value),
    entitySizes,
    businessSectors,
    countryCodes: allSelectedCountries ? Array.from(allSelectedCountries) : undefined,
    continentCodes,
  };
}

export function mapRouteParamsToIncidentsFilters(params: Params): {
  filters: IncidentsFilters;
  pageNumber: number;
  pageSize: number;
  sortBy?: string;
  sortDirection: SortDirection;
} {
  const filters: IncidentsFilters = structuredClone(defaultIncidentsSearchFilters);

  if (params['searchBy']) {
    filters.searchBy.value = params['searchBy'];
  }

  if (params['searchIn']) {
    filters.searchIn.value = params['searchIn'].split(',');
  } else {
    // If no search in is selected, default to search in entity and title
    filters.searchIn.value = [SearchMode.EntityNameAndTitle];
  }

  if (params['geoLocation']) {
    const geoLocationValues = params['geoLocation'].split(',') as string[];

    const countriesInParams: Country[] = geoLocationValues.filter((code) =>
      Object.keys(CountryDescriptor).includes(code),
    ) as Country[];
    const continentsInParams: Continent[] = geoLocationValues.filter((code) =>
      Object.keys(ContinentDescriptor).includes(code),
    ) as Continent[];

    filters.continentCodes.value = continentsInParams;

    const continentAdjustments = continentsInParams.flatMap((continent: Continent) =>
      Array.from(CountriesByContinent[continent]).map((c) => `${continent}:${c}`),
    );
    const continentAdjustedCountries = Object.entries(CountriesByContinent)
      .map(([continent, countries]) =>
        countriesInParams.map((c) => (countries.has(c) ? `${continent}:${c}` : void 0)).filter((c) => !!c),
      )
      .filter((c) => !!c.length)
      .flat() as string[];

    filters.countryCodes.value = [...continentAdjustedCountries, ...continentAdjustments];
  }
  if (params['motivation']) {
    filters.motivation.value = params['motivation'].split(',');
  }
  if (params['businessSector']) {
    filters.businessSector.value = params['businessSector'].split(',');
  }
  if (params['entitySize']) {
    filters.entitySize.value = params['entitySize'].split(',');
  }
  if (params['actorTypes']) {
    filters.actorTypes.value = params['actorTypes'].split(',');
  }
  if (params['revenueFrom']) {
    filters.revenueFrom.value = params['revenueFrom'];
  }
  if (params['revenueTo']) {
    filters.revenueTo.value = params['revenueTo'];
  }
  if (params['dateStart']) {
    filters.dateStart.value = formatAsDateTimeStringIgnoringTheTime(params['dateStart']);
  }
  if (params['dateEnd']) {
    filters.dateEnd.value = formatAsDateTimeStringIgnoringTheTime(params['dateEnd']);
  }
  if (params['incidentDisabled']) {
    filters.incidentDisabled.value = params['incidentDisabled'];
  }
  if (params['incidentTypes']) {
    filters.incidentTypes.value = params['incidentTypes'].split(',') as IncidentType[];
  }
  if (params['dnbId']) {
    filters.dnbId.value = params['dnbId'];
  }
  if (params['includeSubsidiaries']) {
    filters.includeSubsidiaries.value = params['includeSubsidiaries'] === 'true';
  }

  let pageNumber = parseInt(params['pageNumber']);
  let pageSize = parseInt(params['pageSize']);
  pageNumber = Number.isInteger(pageNumber) && pageNumber > 0 ? pageNumber : 1;
  pageSize = Number.isInteger(pageSize) && pageSize > 0 ? pageSize : 10;
  const sortBy = params['sortBy'];
  const sortDirection = params['sortDirection'];

  return {
    filters,
    pageNumber,
    pageSize,
    sortBy,
    sortDirection,
  };
}

export function mapIncidentsFiltersToModel(filters: IncidentsFilters): IncidentsFiltersModel {
  // We need to split the countries as they have the continent written before
  const allSelectedCountries = !filters.countryCodes.value
    ? undefined
    : filters.countryCodes.value.map((c) => c.split(':')[1]);
  const includeDisabledFilter =
    typeof filters.incidentDisabled.value !== 'undefined' &&
    filters.incidentDisabled.value !== '' &&
    filters.incidentDisabled?.value?.toLowerCase() !== 'all';

  return {
    searchBy: filters.searchBy.value || undefined,
    searchIn: filters.searchIn.value as SearchMode[],
    motivations: filters.motivation.value as Motivation[],
    businessSectors: filters.businessSector.value as BusinessSector[],
    entitySizes: filters.entitySize.value as EntitySize[],
    actorTypes: filters.actorTypes.value as ActorType[],
    revenueFrom: !filters.revenueFrom.value ? undefined : parseFloat(filters.revenueFrom.value),
    revenueTo: !filters.revenueTo.value ? undefined : parseFloat(filters.revenueTo.value),
    startDate: !filters.dateStart.value ? undefined : filters.dateStart.value,
    endDate: !filters.dateEnd.value ? undefined : filters.dateEnd.value,
    countryCodes: allSelectedCountries as Country[],
    continentCodes: filters.continentCodes.value as Continent[],
    ...(includeDisabledFilter
      ? {
          status:
            filters.incidentDisabled.value.toLowerCase() === 'true' ? IncidentStatus.Disabled : IncidentStatus.Active,
        }
      : {}),
    incidentTypes: filters.incidentTypes.value as IncidentType[],
    dnbId: filters.dnbId.value || undefined,
    includeSubsidiaries: filters.includeSubsidiaries.value,
  };
}

export function mapIncidentsFiltersToRouteParams(
  filters: IncidentsFilters,
  pageNumber: number,
  pageSize: number,
  sortBy: string | undefined,
  sortDirection: SortDirection,
): Params {
  const geoLocations: string[] = getGeoLocationsFromFilters(filters.continentCodes.value, filters.countryCodes.value);
  const searchBy: string = filters.searchBy.value;

  return {
    searchBy: searchBy || undefined,
    searchIn: filters.searchIn.value.length ? filters.searchIn.value.join(',') : undefined,
    geoLocation: geoLocations.length ? geoLocations.join(',') : undefined,
    motivation: filters.motivation.value.length ? filters.motivation.value.join(',') : undefined,
    businessSector: filters.businessSector.value.length ? filters.businessSector.value.join(',') : undefined,
    entitySize: filters.entitySize.value.length ? filters.entitySize.value.join(',') : undefined,
    actorTypes: filters.actorTypes.value.length ? filters.actorTypes.value.join(',') : undefined,
    revenueFrom: filters.revenueFrom.value || undefined,
    revenueTo: filters.revenueTo.value || undefined,
    dateStart: filters.dateStart.value ? formatAsDateTimeStringIgnoringTheTime(filters.dateStart.value) : undefined,
    dateEnd: filters.dateEnd.value ? formatAsDateTimeStringIgnoringTheTime(filters.dateEnd.value) : undefined,
    incidentDisabled: filters.incidentDisabled?.value || undefined,
    incidentTypes: filters.incidentTypes.value.length ? filters.incidentTypes.value.join(',') : undefined,
    dnbId: filters.dnbId.value || undefined,
    includeSubsidiaries: filters.dnbId.value ? filters.includeSubsidiaries.value : undefined,
    pageNumber,
    pageSize,
    sortBy,
    sortDirection: sortDirection === '' ? undefined : sortDirection,
  };
}

export const buildContinents = (): {
  name: Continent;
  label: string;
  countries: Country[];
  continentAdjustedCountries: string[];
}[] => {
  return (Object.entries(CountriesByContinent) as [Continent, Set<Country>][]).map(([name, countries]) => ({
    name,
    label: ContinentDescriptor[name],
    countries: [...countries].sort((a, b) => CountryDescriptor[a].localeCompare(CountryDescriptor[b])),
    continentAdjustedCountries: [
      ...Array.from(countries)
        .sort((a, b) => CountryDescriptor[a].localeCompare(CountryDescriptor[b]))
        .map((c) => `${name}:${c}`),
    ],
  }));
};

export const fileToMediaFileModelAsync = async (mediaReferences: File[] | null): Promise<MediaFileModel[]> => {
  const toBase64 = async (file: File): Promise<string> => {
    const buffer = await file.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (const byte of bytes) binary += String.fromCharCode(byte);
    return btoa(binary);
  };

  return await Promise.all(
    (mediaReferences ?? []).map(
      async (mediaReference) =>
        ({
          fileName: mediaReference.name,
          contentType: mediaReference.type,
          base64Content: await toBase64(mediaReference),
        }) as MediaFileModel,
    ),
  );
};

function getGeoLocationsFromFilters(continentCodes: string[], countryCodes: string[]): string[] {
  const allSelectedContinents = continentCodes;
  const allSelectedCountries = Array.from(new Set(countryCodes.map((c) => c.split(':')[1] as Country)));

  const countriesAlreadyIncludedViaContinent = allSelectedContinents.flatMap((continent) => [
    ...CountriesByContinent[continent as Continent],
  ]);

  return [
    ...allSelectedContinents,
    ...allSelectedCountries.filter((country) => !countriesAlreadyIncludedViaContinent.includes(country as Country)),
  ];
}
